'use client';

import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Chart } from './Chart';
import { ChartHamburgerMenu } from './combined/ChartHamburgerMenu';
import { ChartSkeleton } from './ChartSkeleton';
import { Breadcrumbs } from '@/components/ui-components/Breadcrumbs/Breadcrumbs';
import { theme } from '../../config/theme';
import type { ChartData, ChartLayout, ChartConfig } from '../../types/chart';

const PieChartContainer = styled.div<{ $isFullscreen: boolean }>`
  overflow: visible;
  transition: all 0.3s ease;
  background: transparent;
  height: ${({ $isFullscreen }) => ($isFullscreen ? '100vh' : 'auto')};
  width: ${({ $isFullscreen }) => ($isFullscreen ? '100vw' : 'auto')};
  display: ${({ $isFullscreen }) => ($isFullscreen ? 'flex' : 'block')};
  flex-direction: ${({ $isFullscreen }) => ($isFullscreen ? 'column' : 'initial')};
  padding: ${({ $isFullscreen }) => ($isFullscreen ? '40px' : '0')};
  background: ${({ $isFullscreen }) => ($isFullscreen ? theme.colors.white : 'transparent')};
`;

const ChartHeader = styled.div`
  padding: 16px 20px 8px 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const ChartTitle = styled.h3`
  display: block;
  margin: 0;
  font-size: 0.75rem;
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.gray[400]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ChartDescription = styled.p`
  display: block;
  margin: 4px 0 0 0;
  font-size: 0.875rem;
  color: ${theme.colors.gray[500]};
`;

const TitleWrapperBottom = styled.div`
  width: calc(100% + 30px);
  padding: 16px 24px 0 3px;
  margin-left: -30px;
  background: ${theme.colors.white};
  border-bottom: 1px solid ${theme.colors.border.primary};
  box-sizing: border-box;
`;

const ChartContent = styled.div<{ $isFullscreen: boolean }>`
  padding: 8px 16px 16px 16px;
  min-height: ${({ $isFullscreen }) => ($isFullscreen ? 'auto' : '300px')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex: ${({ $isFullscreen }) => ($isFullscreen ? '1' : 'initial')};
  
  /* Chart should take all available space */
  & > div {
    flex: 1;
    height: 100%;
  }
`;

export interface PieChartData {
  labels: string[];
  values: number[];
}

export interface PieChartProps {
  title: string;
  description?: string;
  data: PieChartData;
  colors?: string[];
  showLabels?: boolean;
  height?: number;
  loading?: boolean;
  className?: string;
  showBreadcrumb?: boolean; // Optional: show title as breadcrumb instead of chart header
}

export const PieChart: React.FC<PieChartProps> = ({
  title,
  description,
  data,
  colors,
  showLabels = true,
  height = 400,
  loading = false,
  className,
  showBreadcrumb = false,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [zoomEnabled, setZoomEnabled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chartKey, setChartKey] = useState(0);

  // Show skeleton if loading
  if (loading) {
    return <ChartSkeleton type="pie" height={`${height}px`} showLegend={showLabels} />;
  }

  // Monitor fullscreen changes for THIS specific chart only
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (!chartRef.current) return;
      
      const doc = document as Document & {
        webkitFullscreenElement?: Element;
        mozFullScreenElement?: Element;
        msFullscreenElement?: Element;
      };
      
      const fullscreenElement = 
        document.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement;
      
      // Only update if THIS chart is the one in fullscreen (or exiting fullscreen)
      const isThisChartFullscreen = fullscreenElement === chartRef.current;
      const wasThisChartFullscreen = isFullscreen;
      
      if (isThisChartFullscreen !== wasThisChartFullscreen) {
        setIsFullscreen(isThisChartFullscreen);
        // Force re-render by changing key with a small delay to ensure proper layout reset
        setTimeout(() => {
          setChartKey(prev => prev + 1);
        }, 100);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [isFullscreen]);

  const defaultColors = colors || [
    theme.colors.primary[500],
    theme.colors.success,
    theme.colors.warning,
    theme.colors.error,
    theme.colors.primary[300],
    '#8b5cf6',
    '#06b6d4',
    '#84cc16',
  ];

  const total = data.values.reduce((sum, value) => sum + value, 0);

  const chartData: ChartData[] = [{
    type: 'pie',
    labels: data.labels,
    values: data.values,
    marker: {
      color: defaultColors,
      line: {
        color: theme.colors.white,
        width: 2,
      },
    },
    textinfo: showLabels ? 'label+percent' : 'none',
    textposition: 'auto',
    textfont: {
      size: 12,
      color: theme.colors.white,
      family: 'Arial, sans-serif',
    },
    hoverinfo: 'label+percent',
    hovertemplate: '<b>%{label}</b><br>Value: %{value}<br>Percentage: %{percent}<extra></extra>',
  }];

  // Adjust height based on fullscreen state
  const chartHeight = isFullscreen && typeof window !== 'undefined' 
    ? window.innerHeight - 150 
    : height;

  const layout: ChartLayout = {
    height: chartHeight,
    hovermode: 'closest',
    margin: {
      l: 20,
      r: 20,
      t: 20,
      b: 20,
    },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    font: {
      color: theme.colors.gray[700],
      family: 'Arial, sans-serif',
    },
    showlegend: true,
    legend: {
      layout: 'vertical',
      x: 1.05,
      y: 0.5,
      bgcolor: 'rgba(0,0,0,0)',
      font: {
        color: theme.colors.gray[700],
      },
    },
  };

  const config: ChartConfig = {
    displayModeBar: false,
    displaylogo: false,
    staticPlot: !zoomEnabled,
    responsive: true,
    scrollZoom: zoomEnabled,
    doubleClick: zoomEnabled ? 'reset' : false,
    modeBarButtonsToRemove: zoomEnabled 
      ? ['lasso2d', 'select2d', 'autoScale2d', 'resetScale2d']
      : ['lasso2d', 'select2d', 'pan2d', 'zoom2d', 'autoScale2d', 'resetScale2d'],
  };

  // Create table data for hamburger menu
  const tableData: Array<Record<string, string | number>> = data.labels.map((label, index) => {
    const value = data.values[index];
    const percentage = ((value / total) * 100).toFixed(1);
    return {
      label,
      value,
      percentage: `${percentage}%`
    };
  });

  return (
    <PieChartContainer ref={chartRef} $isFullscreen={isFullscreen} className={className}>
      {/* Breadcrumb - shown when showBreadcrumb is true */}
      {showBreadcrumb && (
        <TitleWrapperBottom>
          <Breadcrumbs items={[{ label: title }]} />
        </TitleWrapperBottom>
      )}
      
      {/* Chart Header - shown when showBreadcrumb is false */}
      {!showBreadcrumb && (
        <ChartHeader>
          <HeaderContent>
            <ChartTitle>{title}</ChartTitle>
            {description && <ChartDescription>{description}</ChartDescription>}
          </HeaderContent>
          <ChartHamburgerMenu 
            chartTitle={title}
            tableData={tableData}
            chartContainerRef={chartRef}
            onZoomToggle={setZoomEnabled}
            initialZoomEnabled={zoomEnabled}
          />
        </ChartHeader>
      )}
      
      <ChartContent $isFullscreen={isFullscreen}>
            <Chart
              key={chartKey}
              data={chartData}
              layout={layout}
              config={config}
              revision={chartKey}
            />
      </ChartContent>

    </PieChartContainer>
  );
};